/**
 * Agente Hugi — a tela do assistente dentro do atendimento.
 *
 * ## O que esta tela existe para não deixar acontecer
 *
 * O cliente liga o assistente, testa com o próprio celular no dia seguinte, nada acontece, e conclui que o
 * produto está quebrado. Não está: fora da janela de 24 horas do WhatsApp **ninguém** responde — nem o
 * assistente, nem a equipe. É regra do canal.
 *
 * Por isso os impedimentos aparecem **antes** do formulário, separados por quem resolve cada um:
 *
 * | o que falta | quem resolve |
 * | --- | --- |
 * | chave do provedor de linguagem | nós, no servidor |
 * | instruções e quando atende | o cliente, aqui |
 * | a janela do canal | ninguém — é a regra |
 *
 * Um único interruptor "ligado/desligado" diria "ligado" para um assistente incapaz de emitir uma palavra.
 *
 * ## O que a tela NÃO tem
 *
 * **Nenhum campo de chave de API.** Chave de provedor é do perímetro. Um "cole sua chave da OpenAI" aqui
 * faria o cliente colar segredo num formulário web e nos faria guardá-lo.
 *
 * **Nenhum controle de modelo, temperatura ou tokens.** É mesa de mixagem: mal mexida, degrada a resposta
 * sem sinal nenhum, e o dono de clínica não tem como saber que foi ele.
 */

import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Botao, EstadoCarregando, EstadoDaFalha, Selo, falhaDe, identificadorLegivel, Escolha, Porque, Ladrilho } from "@hugi/ui";
import { Abas, BarraDeAcoes, Destaque, Dica, Gaveta as GavetaV2, PaginaDeRegistro } from "@hugi/ui/kit";
import "./automacoes.css";
import { Bot, Plus, Radio, Smartphone, Sparkles } from "lucide-react";
import {
  acrescentarConhecimento,
  gerarParesDoConhecimento,
  apagarConhecimento,
  arquivarAgente,
  criarAgente,
  apagarDiretriz,
  apagarSimulacao,
  despublicarAgente,
  publicarAgente,
  rodarSimulacoes,
  salvarProcedimento,
  apagarProcedimento,
  salvarTarefa,
  apagarTarefa,
  salvarAgente,
  salvarDiretriz,
  salvarSimulacao,
  useAgente,
  useAgentes,
  useConexoes,
  type ResumoDoAgente,
  type Conhecimento,
  type Procedimento,
  type TarefaDoAgente,
  type Diretriz,
  type PublicacaoDoAgente,
  type QuandoAtende,
  type Simulacao,
  enderecoDeExportacao,
  salvarRegraDoAgente,
  apagarRegraDoAgente,
  type RegraDoAgente,
  type ConfiguracaoDoAgente,
  type PoliticaDeMidia,
} from "./dados.js";

const QUANDO: readonly { id: QuandoAtende; rotulo: string; frase: string }[] = [
  {
    id: "nunca",
    rotulo: "Desligado",
    frase: "O assistente não escreve em nenhuma conversa. É o padrão, e continua sendo até você mudar.",
  },
  {
    id: "fora_do_horario",
    rotulo: "Fora do horário",
    frase:
      "Responde quando a sua equipe não está. Dentro do horário de atendimento, quem responde é gente — " +
      "o assistente nem tenta.",
  },
  {
    id: "sempre",
    rotulo: "Sempre",
    frase:
      "Responde primeiro, inclusive no horário comercial, e passa para uma pessoa quando trava. Serve " +
      "para triagem; significa que o cliente fala com o robô antes de falar com alguém.",
  },
];

/*
  AS ABAS DO ASSISTENTE (spec 23, jornada 1 e D2 — aprovada pelo owner em 01/11).

  "Responde sozinho" exigia DOIS controles em abas diferentes: *quando ele atende* (na 1ª aba, padrão `nunca`) e
  *publicar num número* (na 6ª). Um sem o outro é recusa calada no runtime (`src/canal/automacao.ts`) — é a raiz
  das 142 respostas perdidas. Os dois agora moram na MESMA aba, a primeira, e o primário do cabeçalho ("Ligar")
  grava um e faz o outro num clique. `publicar` continua aceito na URL e cai nesta aba.
*/
type AbaDoAgente = "quando" | "comportamento" | "quando_passa" | "conhecimento" | "acoes" | "testes" | "atividade";
const ABAS_DO_AGENTE: readonly { id: AbaDoAgente; rotulo: string }[] = [
  { id: "quando", rotulo: "Quando ele responde" },
  { id: "comportamento", rotulo: "Como ele atende" },
  { id: "quando_passa", rotulo: "Quando ele passa" },
  { id: "conhecimento", rotulo: "O que ele sabe" },
  { id: "acoes", rotulo: "O que ele faz" },
  { id: "testes", rotulo: "Casos de teste" },
  { id: "atividade", rotulo: "Atividade" },
];

/** `?aba=` na URL (contrato das rotas de tela da spec 23): quem chega do Início ou de Conectar abre na aba certa. */
export function abaDoAgenteNaUrl(busca: string): AbaDoAgente | null {
  const pedida = new URLSearchParams(busca).get("aba");
  if (!pedida) return null;
  if (pedida === "publicar") return "quando";
  return ABAS_DO_AGENTE.some((a) => a.id === pedida) ? (pedida as AbaDoAgente) : null;
}

/** `?atende=` na URL: o ladrilho "Responder fora do horário" de Automações chega com a escolha feita. */
function atendeNaUrl(busca: string): QuandoAtende | null {
  const a = new URLSearchParams(busca).get("atende");
  return a === "fora_do_horario" || a === "sempre" ? a : null;
}

const ROTULO_DO_ATENDE: Readonly<Record<QuandoAtende, string>> = {
  nunca: "Desligado",
  fora_do_horario: "Fora do horário",
  sempre: "Sempre",
};

/**
 * O SELO ÚNICO do assistente (spec 23): publicação E horário, juntos — o mesmo critério na tela do assistente e na
 * lista de Automações.
 *
 * O selo antigo dizia "Respondendo" com `atende` ligado e provedor configurado, e ignorava a publicação: sem
 * número publicado o runtime recusa (*"O assistente não está publicado neste número"*), e a tela dizia que estava
 * no ar. Agora:
 *
 * | publicado | atende | selo |
 * |---|---|---|
 * | não | — | Sem número |
 * | sim | nunca | Publicado, mas desligado |
 * | sim | ligado, sem provedor | Publicado, mas não responde |
 * | sim | ligado | No ar em +55… |
 *
 * ⚠️ `atende` é o SALVO; o runtime lê o PUBLICADO (a publicação congela a configuração). Quando há alteração salva
 * depois da publicação, a dica diz isso — e o cabeçalho oferece "Publicar alterações".
 */
export function seloDoAssistente(e: {
  readonly numeros: readonly string[];
  readonly atende: QuandoAtende;
  readonly provedorConfigurado?: boolean;
  readonly alteracoesPorPublicar?: boolean;
}): { readonly rotulo: string; readonly estado: "ok" | "degradado" | "neutro"; readonly dica: string } {
  if (e.numeros.length === 0) {
    return { rotulo: "Sem número", estado: "neutro", dica: "Não está publicado em número nenhum: só responde quando uma automação o chama." };
  }
  const onde = e.numeros.length === 1 ? identificadorLegivel(e.numeros[0]!) : `${e.numeros.length} números`;
  const pendente = e.alteracoesPorPublicar ? " Há alterações salvas que ainda não estão no número." : "";
  if (e.atende === "nunca") {
    return { rotulo: "Publicado, mas desligado", estado: "degradado", dica: `Publicado em ${onde} com "Desligado": não responde ninguém.${pendente}` };
  }
  if (e.provedorConfigurado === false) {
    return { rotulo: "Publicado, mas não responde", estado: "degradado", dica: `Falta a chave do provedor de linguagem, no servidor.${pendente}` };
  }
  return { rotulo: `No ar em ${onde}`, estado: "ok", dica: `Responde ${ROTULO_DO_ATENDE[e.atende].toLowerCase()} em ${onde}.${pendente}` };
}

/**
 * O ASSISTENTE — a tela dele, hoje dentro de Automações.
 *
 * `aoVoltar` é o que separa os dois contextos, e a diferença não é cosmética: dentro de Automações a
 * **lista** de assistentes vive na tela de fora, então o seletor de ladrilhos sai daqui (dois lugares
 * escolhendo a mesma coisa na mesma tela é como uma delas fica desatualizada), e entra a frase que
 * diz **quem usa este assistente** — que é a relação que faltava e que fez o owner se perder.
 */
export function Agente(props: {
  readonly agenteId?: string | null;
  readonly aoVoltar?: () => void;
  readonly usadoPor?: readonly { readonly id: string; readonly nome: string }[];
  readonly aoAbrirAutomacao?: (id: string) => void;
} = {}) {
  const [aba, setAba] = useState<AbaDoAgente>(() => (typeof window === "undefined" ? null : abaDoAgenteNaUrl(window.location.search)) ?? "quando");
  const cliente = useQueryClient();
  const conexoes = useConexoes();
  // `null` = o principal; o servidor decide qual é. Trocar de agente refaz a consulta inteira.
  const [agenteEscolhido, setAgenteEscolhido] = useState<string | null>(props.agenteId ?? null);
  const q = useAgente(agenteEscolhido);
  // O id REAL do agente que a tela está mostrando — nunca `null`, porque o servidor resolve o principal.
  const agenteId = q.data?.agente?.id ?? agenteEscolhido;

  const [nome, setNome] = useState("");
  const [atende, setAtende] = useState<QuandoAtende>("nunca");
  const [instrucoes, setInstrucoes] = useState("");
  const [turnos, setTurnos] = useState(10);
  const [escape, setEscape] = useState("");
  const [filaId, setFilaId] = useState("");
  const [etiquetaId, setEtiquetaId] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [canaisAbertos, setCanaisAbertos] = useState(false);
  // O "Ligar" (D2): o número escolhido, e o que ficou da última tentativa.
  const [conexaoEscolhida, setConexaoEscolhida] = useState("");
  const [ligando, setLigando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  /** O "quando" já foi gravado e só a publicação falhou: tentar de novo não regrava. */
  const [soFaltaPublicar, setSoFaltaPublicar] = useState(false);
  /** O número tem OUTRO assistente no ar: o próximo clique confirma a substituição. */
  const [precisaConfirmar, setPrecisaConfirmar] = useState(false);

  const config = q.data?.configuracao;
  // Semeia o formulário quando o servidor responde. `config` como dependência e não `q.data`: o resto do
  // corpo muda a cada evento e reescreveria o que a pessoa está digitando.
  useEffect(() => {
    if (!config) return;
    setNome(config.nome);
    setAtende(config.atende);
    setInstrucoes(config.instrucoes ?? "");
    setTurnos(config.maxTurnosSemHumano);
    setEscape(config.palavrasDeEscape.join(", "));
    setFilaId(config.filaDeEscalonamentoId ?? "");
    setEtiquetaId(config.etiquetaId ?? "");
    setSalvo(false);
    setErro(null);
    // Quem chegou pelo ladrilho "Responder fora do horário" já vem com a escolha feita — ainda não gravada.
    const pedido = config.atende === "nunca" && typeof window !== "undefined" ? atendeNaUrl(window.location.search) : null;
    if (pedido) setAtende(pedido);
  }, [config]);

  if (q.isPending) return <EstadoCarregando rotulo="Carregando a configuração do assistente" />;
  if (q.isError) return <EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} />;

  const { estado, filas, etiquetas } = q.data;

  /*
    AS PUBLICAÇÕES DESTE assistente — e não as da empresa. A rota devolve todas (de qualquer agente), e o campo
    "Publicado em" mostrava o número da recepção na tela da cobrança. Publicação sem agente (inserida por fora) é
    do principal.
  */
  const proprias = q.data.publicacoes.filter((p) => (p.agenteId ?? (q.data.agente?.principal ? agenteId : null)) === agenteId);
  const ativas = (conexoes.data?.conexoes ?? []).filter((c) => c.estado !== "revogada" && c.estado !== "desconectada");
  const rotuloDaConexao = (id: string): string => {
    const c = ativas.find((x) => x.id === id);
    if (c) return c.apelido?.trim() || (c.numeroExibicao ? identificadorLegivel(c.numeroExibicao) : c.canal);
    const p = proprias.find((x) => x.conexaoId === id);
    return p ? identificadorLegivel(p.conexaoRotulo) : "o número";
  };
  // O número do "Ligar": o escolhido; senão o que ele já ocupa; senão o único que existe.
  const conexaoAlvo = conexaoEscolhida || proprias[0]?.conexaoId || (ativas.length === 1 ? ativas[0]!.id : "");

  const ultimaPublicacao = proprias.reduce<string | null>((m, p) => (m === null || p.publicadoEm > m ? p.publicadoEm : m), null);
  /** Configuração salva DEPOIS da última publicação: o número ainda fala a versão anterior. */
  const alteracoesPorPublicar = ultimaPublicacao !== null && !!config?.atualizadoEm && config.atualizadoEm > ultimaPublicacao;
  /** O formulário difere do que está gravado. */
  const sujo =
    !!config &&
    (nome !== config.nome ||
      atende !== config.atende ||
      instrucoes !== (config.instrucoes ?? "") ||
      turnos !== config.maxTurnosSemHumano ||
      escape !== config.palavrasDeEscape.join(", ") ||
      filaId !== (config.filaDeEscalonamentoId ?? "") ||
      etiquetaId !== (config.etiquetaId ?? ""));
  const selo = seloDoAssistente({
    numeros: proprias.map((p) => p.conexaoRotulo),
    atende: config?.atende ?? "nunca",
    provedorConfigurado: estado.provedor.configurado,
    alteracoesPorPublicar,
  });

  /*
    O CORPO INTEIRO, sempre — e não só o campo que mudou.

    `gravarConfiguracaoDoAgente` é um `UPDATE` de todas as colunas: mandar só a política de mídia
    apagaria o nome e as instruções. É a mesma armadilha do `EXCLUDED.*` que a franquia no console
    pagou em 02/09, do outro lado.
  */
  const corpoDaConfiguracao = (): Record<string, unknown> => ({
    nome,
    atende,
    instrucoes: instrucoes.trim() || null,
    maxTurnosSemHumano: turnos,
    palavrasDeEscape: escape
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p !== ""),
    filaDeEscalonamentoId: filaId || null,
    etiquetaId: etiquetaId || null,
    midiaImagem: config?.midiaImagem ?? "transbordar",
    midiaArquivo: config?.midiaArquivo ?? "evasiva",
    falaMidiaImagem: config?.falaMidiaImagem ?? null,
    falaMidiaArquivo: config?.falaMidiaArquivo ?? null,
    falaArquivoNaoLido: config?.falaArquivoNaoLido ?? null,
  });

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    setSalvo(false);
    const r = await salvarAgente(cliente, agenteId, corpoDaConfiguracao());
    setSalvando(false);
    if (r.ok) {
      setSalvo(true);
      return;
    }
    setErro(r.falha?.erro.mensagem ?? "Não foi possível salvar.");
  };

  /*
    LIGAR (D2, aprovada pelo owner em 01/11): grava o "quando ele responde" escolhido E publica no número — as duas
    chamadas que já existiam, num clique. Se a segunda falhar, a tela diz o que ficou feito (o "quando" gravado) e o
    próximo clique só tenta publicar de novo.
  */
  const ligar = async () => {
    setErro(null);
    setAviso(null);
    setSalvo(false);
    if (atende === "nunca") {
      setAba("quando");
      setErro("Escolha quando ele responde: fora do horário ou sempre.");
      return;
    }
    if (!conexaoAlvo) {
      setAba("quando");
      setErro(ativas.length === 0 ? "Conecte um número antes de ligar." : "Escolha em qual número ele responde.");
      return;
    }
    setLigando(true);
    if (!soFaltaPublicar || sujo) {
      const s = await salvarAgente(cliente, agenteId, corpoDaConfiguracao());
      if (!s.ok) {
        setLigando(false);
        setErro(s.falha?.erro.mensagem ?? "Não foi possível salvar.");
        return;
      }
    }
    const jaEhDele = proprias.some((p) => p.conexaoId === conexaoAlvo);
    const r = await publicarAgente(cliente, agenteId, { conexaoId: conexaoAlvo, confirmarSubstituicao: precisaConfirmar || jaEhDele });
    setLigando(false);
    if (r.ok) {
      setSoFaltaPublicar(false);
      setPrecisaConfirmar(false);
      setAviso(`Ligado: responde ${ROTULO_DO_ATENDE[atende].toLowerCase()} em ${rotuloDaConexao(conexaoAlvo)}.`);
      return;
    }
    setSoFaltaPublicar(true);
    if (r.falha?.erro.erro === "recusado_pelo_dominio") {
      // Outro assistente atende este número: a frase do servidor nomeia quem, e o próximo clique confirma.
      setPrecisaConfirmar(true);
      setErro(r.falha.erro.mensagem ?? "Outro assistente atende este número. Confirme para substituir.");
      return;
    }
    setErro(
      `Gravei "${ROTULO_DO_ATENDE[atende]}", mas não consegui publicar em ${rotuloDaConexao(conexaoAlvo)}: ` +
        `${r.falha?.erro.mensagem ?? "o servidor não respondeu"}. Tente de novo.`,
    );
  };

  /** Publicar alterações: salva (se há o que salvar) e republica nos números em que ele já está. */
  const publicarAlteracoes = async () => {
    setErro(null);
    setAviso(null);
    setSalvo(false);
    setLigando(true);
    if (sujo) {
      const s = await salvarAgente(cliente, agenteId, corpoDaConfiguracao());
      if (!s.ok) {
        setLigando(false);
        setErro(s.falha?.erro.mensagem ?? "Não foi possível salvar.");
        return;
      }
    }
    const falhas: string[] = [];
    for (const p of proprias) {
      const r = await publicarAgente(cliente, agenteId, { conexaoId: p.conexaoId, confirmarSubstituicao: true });
      if (!r.ok) falhas.push(`${identificadorLegivel(p.conexaoRotulo)}: ${r.falha?.erro.mensagem ?? "sem resposta"}`);
    }
    setLigando(false);
    if (falhas.length === 0) {
      setAviso("Alterações publicadas. O número já responde com a versão nova.");
      return;
    }
    setErro(`${sujo ? "Salvei, mas não" : "Não"} publiquei em ${falhas.join("; ")}. Tente de novo.`);
  };

  /*
    O PRIMÁRIO SEGUE O ESTADO (spec 23): sem número ou desligado → Ligar; no ar com alteração → Publicar alterações;
    no ar e em dia → nenhum (Salvar fica como ação comum).
  */
  const noArDeVerdade = proprias.length > 0 && atende !== "nunca";
  const primaria: { rotulo: string; aoClicar: () => void } | null = !noArDeVerdade
    ? {
        rotulo: ligando ? "Ligando…" : precisaConfirmar ? "Confirmar e substituir" : soFaltaPublicar ? "Tentar publicar de novo" : "Ligar",
        aoClicar: () => void ligar(),
      }
    : sujo || alteracoesPorPublicar
      ? { rotulo: ligando ? "Publicando…" : "Publicar alterações", aoClicar: () => void publicarAlteracoes() }
      : null;

  const exportar = () => {
    /*
      EXPORTAR leva a base de conhecimento junto — e ela é conteúdo desta empresa. Por isso a
      confirmação diz o que vai no arquivo, em vez de baixar calado: quem manda um assistente
      para outro cliente precisa saber que está mandando a tabela de preços dentro dele.
    */
    const comBase = window.confirm(
      "Exportar este assistente.\n\nO arquivo leva as instruções, as diretrizes, os procedimentos e as " +
        "tarefas — e também a BASE DE CONHECIMENTO, que é conteúdo desta empresa, com os CASOS DE TESTE " +
        "que dependem dela.\n\n" +
        "OK para levar a base e os casos juntos. Cancelar para exportar sem eles.",
    );
    window.location.href = enderecoDeExportacao({ agenteId, conhecimento: comBase });
  };

  const titulo = props.aoVoltar ? (config?.nome ?? "Assistente") : "Agente Hugi";
  const usos = props.usadoPor ?? [];
  const respondeSozinho = estado.configuracao.ligado
    ? "E ele responde sozinho nos números publicados — nesse caso, só quando nenhuma automação respondeu à mensagem."
    : "Ele não responde sozinho em número nenhum: só roda quando uma automação o chama.";

  /*
    DETALHE (spec 22): trilha, destaque e abas fixos; o conteúdo da aba rola dentro do painel. O que era texto
    solto acima das abas (quem usa, a chave do provedor, as regras por canal) virou campo do destaque, faixa dele
    ou gaveta — nada sumiu, e nada empurra a página.
  */
  return (
    <PaginaDeRegistro
      idDoTitulo="hugi-agente-titulo"
      trilha={
        props.aoVoltar
          ? [{ rotulo: "Automações", href: "/automacoes", aoClicar: props.aoVoltar }, { rotulo: titulo }]
          : [{ rotulo: titulo }]
      }
      destaque={
        <Destaque
          icone={<Sparkles size={18} />}
          tipo="Assistente"
          titulo={titulo}
          idDoTitulo="hugi-agente-titulo"
          campos={[
            {
              rotulo: "Estado",
              // O selo ÚNICO (spec 23): publicação e horário juntos. Ver `seloDoAssistente`.
              valor: <Selo rotulo={selo.rotulo} estado={selo.estado} />,
              dica: selo.dica,
            },
            /*
              QUEM USA ESTE ASSISTENTE — o campo que responde *"e se eu mexer aqui, o que muda?"*.
              Ele diz as duas metades porque elas são diferentes e convivem: as automações que o chamam num
              passo, e os números em que ele responde SOZINHO. A segunda tem a ressalva que o owner não
              sabia: com número, ele responde **depois** das automações — só quando nenhuma respondeu.
            */
            ...(props.aoVoltar
              ? [
                  {
                    rotulo: "Usado por",
                    ausente: usos.length === 0,
                    dica: usos.length > 0 ? usos.map((f) => f.nome).join(", ") : "Nenhuma automação chama este assistente num passo.",
                    valor:
                      usos.length > 0 ? (
                        <span className="hugi-agente-usos">
                          {usos.map((f, i) => (
                            <span key={f.id}>
                              {i > 0 ? ", " : ""}
                              <button type="button" onClick={() => props.aoAbrirAutomacao?.(f.id)}>
                                {f.nome}
                              </button>
                            </span>
                          ))}
                        </span>
                      ) : (
                        "Nenhuma automação"
                      ),
                  },
                  {
                    rotulo: "Responde sozinho",
                    valor: estado.configuracao.ligado ? "Nos números publicados, depois das automações" : "Em número nenhum",
                    dica: respondeSozinho,
                  },
                ]
              : []),
            /*
              PUBLICADO EM (spec 23, J2): publicar é o que põe o assistente para atender, e era a 6ª aba — o gargalo do
              produto é assistente nenhum publicado. Sem número, o campo é o atalho para a aba.
            */
            {
              rotulo: "Publicado em",
              ausente: proprias.length === 0,
              valor:
                proprias.length > 0 ? (
                  proprias.map((p) => identificadorLegivel(p.conexaoRotulo)).join(", ")
                ) : (
                  <button type="button" className="hugi-link-de-campo" onClick={() => setAba("quando")}>
                    Nenhum número — ligar
                  </button>
                ),
            },
            {
              rotulo: "Última alteração",
              valor: config?.atualizadoEm ? new Date(config.atualizadoEm).toLocaleString("pt-BR") : "—",
              ausente: !config?.atualizadoEm,
            },
          ]}
          acoes={
            <BarraDeAcoes
              rotulo="Ações do assistente"
              visiveis={3}
              erro={erro ?? undefined}
              acoes={[
                /*
                  UMA primária, a que o estado pede (Ligar / Publicar alterações / nenhuma). SALVAR continua no
                  destaque (spec 22): ele grava a configuração inteira, e no fim da aba ficava abaixo da dobra.
                */
                ...(primaria ? [{ id: "primaria", rotulo: primaria.rotulo, tom: "primaria" as const, desabilitada: ligando, aoClicar: primaria.aoClicar }] : []),
                { id: "salvar", rotulo: salvando ? "Salvando…" : "Salvar", desabilitada: salvando || ligando, aoClicar: () => void salvar() },
                { id: "exportar", rotulo: "Exportar", aoClicar: exportar },
                ...(estado.canais.length > 0
                  ? [{ id: "canais", rotulo: `O que cada canal permite (${estado.canais.length})`, aoClicar: () => setCanaisAbertos(true) }]
                  : []),
              ]}
            >
              {salvo ? <p className="v2-motivo" role="status">Salvo.</p> : null}
              {aviso ? <p className="v2-motivo" role="status">{aviso}</p> : null}
            </BarraDeAcoes>
          }
          rodape={
            !estado.provedor.configurado || !props.aoVoltar ? (
              <div className="hugi-agente-faixa">
                {/* --- Os impedimentos, antes do formulário ------------------------------------------- */}
                {!estado.provedor.configurado ? (
                  <span>
                    <span className="is-atencao">Falta a chave do provedor de linguagem.</span> {estado.provedor.comoConfigurar}{" "}
                    <Dica texto="Isso é no servidor, não aqui — você pode configurar tudo enquanto isso, e o assistente começa a responder quando a chave existir." />
                  </span>
                ) : null}
                {props.aoVoltar ? null : (
                  <SeletorDeAgentes
                    agentes={q.data.agentes}
                    escolhido={agenteId}
                    cliente={cliente}
                    aoEscolher={(id) => {
                      setAgenteEscolhido(id);
                      setAba("quando");
                    }}
                  />
                )}
              </div>
            ) : undefined
          }
        />
      }
      principal={
        /*
          ABAS, e não uma coluna de 3.297px. Ordem do owner em 03/09: *"a tela do agente está com uma
          rolagem quilométrica que não se justifica, as coisas têm que ter abas"*. Nada foi apagado — mudou
          de aba. Desde a spec 22 são as `Abas` do kit, e o painel delas é o que rola.
        */
        <Abas
          rotulo="Seções do agente"
          abas={ABAS_DO_AGENTE.map((a) => ({ id: a.id, rotulo: a.rotulo }))}
          ativa={aba}
          aoTrocar={(id) => setAba(id as AbaDoAgente)}
          classe="hugi-automacoes-abas"
        >
          {() => (
            <>
      {/*
        As regras por canal ficam atrás de um clique: são verdadeiras, vêm do SERVIDOR (o catálogo é
        quem sabe se o canal tem janela), e não são a primeira coisa a ler — a primeira é "quando ele
        responde". Desde a spec 22, a gaveta do kit (abre pela ação do destaque).
      */}
      <GavetaV2
        aberta={canaisAbertos}
        aoFechar={() => setCanaisAbertos(false)}
        titulo={`O que cada canal permite ao assistente (${estado.canais.length})`}
      >
        <div className="hugi-agente-canais">
          {estado.canais.map((c, i) => (
            <p key={`${c.canal}-${i}`}>
              <strong>{c.rotulo}: </strong>
              {c.aviso}
            </p>
          ))}
        </div>
      </GavetaV2>

      {aba === "quando_passa" ? (
        <RegrasDeTransbordo
          agenteId={agenteId}
          cliente={cliente}
          regras={q.data.regras ?? []}
          config={config}
          aoSalvarPolitica={(mudanca) => void salvarAgente(cliente, agenteId, { ...corpoDaConfiguracao(), ...mudanca })}
        />
      ) : null}

      {aba === "comportamento" ? (
        <>
      {/* Dois cartões lado a lado: quando passa, e quem ele é ("quando responde" virou a 1ª aba, spec 23).
          ⚠️ Os três são filhos DIRETOS da grade, e isso não é estilo: o `[&>*]:min-w-0` só alcança
          filho direto. Havia dois `<div className="contents">` agrupando-os aqui, e `display: contents`
          não tem caixa — a regra caía neles e morria, os cartões ficavam com `min-width: auto` e a
          375px mediam 346px num espaço de 237, vazando pela direita. Não reintroduzir o invólucro. */}
      <div className="grid items-start gap-4 lg:grid-cols-2 [&>*]:min-w-0">
      {/* --- O freio de mão --------------------------------------------------------------------- */}

      <div className="vidro flex flex-col gap-3 p-4">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-medium">Quando ele passa para uma pessoa <Porque texto="Um assistente que nunca desiste é um cliente preso num laço. Estas duas regras são a saída." /></p>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium">Depois de quantas mensagens do cliente</span>
          <input
            type="number"
            className="campo w-24"
            min={1}
            max={20}
            value={turnos}
            onChange={(e) => setTurnos(Number(e.target.value))}
          />
          <span className="text-xs text-[color:var(--tinta-fraca)]">
            Passado esse número de mensagens de quem escreveu, a conversa volta para a fila com todo o
            histórico. Antes disso ele já passa sozinho quando repete a mesma resposta três vezes.
          </span>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium">Frases que interrompem na hora</span>
          <input
            className="campo"
            value={escape}
            onChange={(e) => setEscape(e.target.value)}
            placeholder="atendente, humano, pessoa"
          />
          <span className="text-xs text-[color:var(--tinta-fraca)]">
            Separadas por vírgula. Basta a palavra aparecer na frase — "quero falar com um atendente"
            funciona sem você cadastrar a frase inteira. É o freio que o seu cliente controla.
          </span>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium">Para qual fila ele passa</span>
          <select className="campo" value={filaId} onChange={(e) => setFilaId(e.target.value)}>
            <option value="">A mesma fila de qualquer conversa nova</option>
            {filas.map((f) => (
              <option key={f.id} value={f.id}>
                {f.nome}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium">Etiqueta de quem falou com o assistente</span>
          <select className="campo" value={etiquetaId} onChange={(e) => setEtiquetaId(e.target.value)}>
            <option value="">Nenhuma</option>
            {etiquetas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}
              </option>
            ))}
          </select>
          <span className="text-xs text-[color:var(--tinta-fraca)]">
            É o que torna "foi atendido pelo assistente" pesquisável no inbox e nos relatórios.
          </span>
        </label>
      </div>

      {/* --- Quem ele é ------------------------------------------------------------------------- */}

      <div className="vidro flex flex-col gap-3 p-4">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium">Como ele se apresenta</span>
          <input
            className="campo"
            value={nome}
            maxLength={40}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Assistente"
          />
          <span className="text-xs text-[color:var(--tinta-fraca)]">
            É o nome que o seu cliente lê. Não é cosmético.
          </span>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium">Instruções</span>
          <textarea
            className="campo min-h-40 text-sm leading-relaxed"
            value={instrucoes}
            maxLength={4000}
            onChange={(e) => setInstrucoes(e.target.value)}
            placeholder={
              "O que ele precisa saber sobre o seu negócio, e o que ele NÃO deve fazer.\n\n" +
              "Exemplo:\n" +
              "Você atende a Clínica X. Horário: seg a sex, 8h às 18h.\n" +
              "Pode informar endereço, convênios aceitos e preparo de exame.\n" +
              "NUNCA marque, desmarque ou confirme consulta — passe para uma pessoa.\n" +
              "NUNCA dê orientação médica, nem sugira medicamento ou dosagem."
            }
          />
          <span className="text-xs text-[color:var(--tinta-fraca)]">
            {instrucoes.length}/4000. O mais importante são as frases que começam com NUNCA: é o que
            impede o assistente de responder o que só uma pessoa pode responder.
          </span>
        </label>
      </div>

      </div>

        </>
      ) : null}
      {aba === "conhecimento" ? (
        <>
      <BaseDeConhecimento itens={q.data.conhecimento} cliente={cliente} agenteId={agenteId} />

      <Diretrizes
        itens={q.data.diretrizes}
        tipos={q.data.tiposDeDiretriz}
        cliente={cliente} agenteId={agenteId}
      />

        </>
      ) : null}
      {aba === "acoes" ? (
        <>
      <Procedimentos itens={q.data.procedimentos} cliente={cliente} agenteId={agenteId} />

      <Tarefas
        itens={q.data.tarefas}
        operacoes={q.data.operacoesDeTarefa}
        filas={q.data.filas}
        motivos={q.data.motivos}
        etiquetas={q.data.etiquetas}
        cliente={cliente} agenteId={agenteId}
      />

        </>
      ) : null}
      {aba === "testes" ? (
        <>
      <Simulacoes
        itens={q.data.simulacoes}
        esperas={q.data.esperas}
        temProvedor={estado.provedor.configurado}
        cliente={cliente} agenteId={agenteId}
      />

        </>
      ) : null}
      {aba === "quando" ? (
        <QuandoResponde
          atende={atende}
          aoMudarAtende={(a) => {
            setAtende(a);
            setSoFaltaPublicar(false);
          }}
          conexoes={ativas.map((c) => ({ id: c.id, rotulo: rotuloDaConexao(c.id) }))}
          conexaoAlvo={conexaoAlvo}
          aoEscolherConexao={(id) => {
            setConexaoEscolhida(id);
            setPrecisaConfirmar(false);
          }}
          publicacoes={proprias}
          cliente={cliente}
          agenteId={agenteId}
          carregandoConexoes={conexoes.isPending}
        />
      ) : null}
      {aba === "atividade" ? (
        <>
      {/* --- O que ele fez --------------------------------------------------------------------- */}

      <div className="vidro flex flex-col gap-3 p-4">
        <div>
          <p className="text-sm font-medium">O que ele fez nos últimos 30 dias</p>
          <p className="mt-0.5 text-xs text-[color:var(--tinta-fraca)]">
            <strong>Recusou</strong> é a coluna que mais importa e a menos óbvia: ele quis responder e a
            saída foi barrada — fora da janela do canal, ou porque alguém da equipe já tinha assumido.
          </p>
        </div>
        <div className="flex gap-6">
          <Numero rotulo="Respondeu" valor={estado.contagem.respondeu} />
          <Numero rotulo="Passou para uma pessoa" valor={estado.contagem.passou} />
          <Numero rotulo="Recusou" valor={estado.contagem.recusou} />
        </div>

        {estado.decisoes.length === 0 ? (
          <p className="text-sm text-[color:var(--tinta-fraca)]">
            Nada registrado ainda. Cada decisão do assistente aparece aqui — inclusive as recusas.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
            {estado.decisoes.map((d) => (
              <li key={d.id} className="flex flex-wrap items-baseline gap-2 py-1.5 text-sm">
                <Selo
                  rotulo={
                    d.decisao === "respondeu"
                      ? "Respondeu"
                      : d.decisao === "passou_para_humano"
                        ? "Passou"
                        : "Recusou"
                  }
                  estado={d.decisao === "respondeu" ? "ok" : d.decisao === "recusou" ? "degradado" : "neutro"}
                />
                <span className="text-[color:var(--tinta-fraca)]">{d.motivo ?? "—"}</span>
                <span className="ml-auto shrink-0 text-xs text-[color:var(--tinta-fraca)]">
                  {new Date(d.criadoEm).toLocaleString("pt-BR")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
        </>
      ) : null}
            </>
          )}
        </Abas>
      }
    />
  );
}

/* ================================================================================================
   QUANDO ELE PASSA PARA UMA PESSOA — as regras, e a política de mídia.

   ## O que esta tela existe para tirar do código

   Isto era `src/canal/sinais.ts`: cinco listas com ~70 termos em português — "quero o pacote",
   "esse sábado", "aceita pix", "tem horário" —, escritas para o piloto da fotógrafa e valendo para
   **todo tenant**. E elas não eram contexto para o modelo: `agenteDeveResponder` as lê e **força
   transbordo**, antes dos tetos. Numa clínica, "tem horário na quinta?" — a pergunta que o
   assistente dela existe para responder — passava para uma pessoa por uma lista de fotografia.

   Decisão do owner: *"eu não aceito ter nada chumbado no código… nada nos impede de amanhã ter outro
   tipo de profissional com a mesma característica dela"*.

   ## O que NÃO está aqui, e é o ponto

   Os **tetos** e o **pedido explícito de pessoa** continuam em código. Eles não são do vertical: são
   a promessa de não prender ninguém com o robô, e um cliente que pudesse desligá-los desligaria a
   única saída que a conversa tem.
   ============================================================================================== */

const POLITICAS: readonly { id: PoliticaDeMidia; rotulo: string; frase: string }[] = [
  { id: "transbordar", rotulo: "Passar para uma pessoa", frase: "Vai direto para a equipe, sem o assistente responder." },
  { id: "evasiva", rotulo: "Pedir por texto", frase: "Ele pede uma vez para explicarem por texto; se insistirem, passa." },
  { id: "ignorar", rotulo: "Seguir a conversa", frase: "Ele continua como se não tivesse chegado nada — use quando a mídia é só contexto." },
];

function RegrasDeTransbordo(props: {
  readonly agenteId: string | null;
  readonly cliente: QueryClient;
  readonly regras: readonly RegraDoAgente[];
  readonly config: ConfiguracaoDoAgente | undefined;
  readonly aoSalvarPolitica: (mudanca: Record<string, unknown>) => void;
}) {
  const [editando, setEditando] = useState<string | "nova" | null>(null);
  const [rotulo, setRotulo] = useState("");
  const [termos, setTermos] = useState("");
  const [fala, setFala] = useState("");
  const [ordem, setOrdem] = useState(100);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const abrir = (r: RegraDoAgente | null) => {
    setEditando(r ? r.id : "nova");
    setRotulo(r?.rotulo ?? "");
    setTermos((r?.termos ?? []).join(", "));
    setFala(r?.fala ?? "");
    setOrdem(r?.ordem ?? 100);
    setErro(null);
  };

  const salvar = async () => {
    setOcupado(true);
    setErro(null);
    const r = await salvarRegraDoAgente(props.cliente, props.agenteId, {
      ...(editando && editando !== "nova" ? { id: editando } : {}),
      rotulo: rotulo.trim(),
      termos: termos.split(",").map((t) => t.trim()).filter((t) => t !== ""),
      ordem,
      fala: fala.trim() || null,
    });
    setOcupado(false);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível salvar a regra.");
      return;
    }
    setEditando(null);
  };

  const apagar = async (r: RegraDoAgente) => {
    if (!window.confirm(`Apagar a regra '${r.rotulo}'? O assistente passa a responder sobre isso.`)) return;
    const x = await apagarRegraDoAgente(props.cliente, props.agenteId, r.id);
    if (!x.ok) setErro(x.falha?.erro.mensagem ?? "Não foi possível apagar.");
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="vidro flex flex-col gap-3 p-4">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-medium">
            Sobre o que ele NÃO fala
            <Porque texto="Quando a mensagem contém uma destas expressões, o assistente não responde: ele passa a conversa para uma pessoa e diz a frase que você escrever. É o que impede o robô de inventar data, preço ou prazo." />
          </p>
          <p className="mt-0.5 text-xs text-[color:var(--tinta-fraca)]">
            Cada regra é uma frase inteira: <strong>quando a mensagem contiver isto, passe para uma
            pessoa e diga aquilo</strong>. Elas são desta empresa — um modelo de mercado traz as do
            setor, e você muda o que quiser.
          </p>
        </div>

        {props.regras.length === 0 ? (
          <p className="text-xs" style={{ color: "var(--atencao-texto)" }}>
            Nenhuma regra. O assistente vai tentar responder tudo o que estiver na base — inclusive
            data, preço e prazo, se você os tiver colado lá.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
            {props.regras.map((r) => (
              <li key={r.id} className="flex flex-wrap items-baseline gap-2 py-2">
                <span className="min-w-0 flex-1">
                  <span className="text-sm font-medium">{r.rotulo}</span>
                  {r.deFabrica ? (
                    <span className="ml-2 text-xs text-[color:var(--tinta-fraca)]">veio de um modelo</span>
                  ) : null}
                  <span className="block text-xs text-[color:var(--tinta-fraca)]">
                    {r.termos.slice(0, 6).join(" · ")}
                    {r.termos.length > 6 ? ` · +${r.termos.length - 6}` : ""}
                  </span>
                  <span className="block text-xs text-[color:var(--tinta-fraca)]">
                    {r.fala ? `Diz: "${r.fala}"` : "Diz a frase padrão do Hugi."}
                  </span>
                </span>
                <span className="flex shrink-0 gap-2">
                  <button type="button" className="text-xs underline" onClick={() => abrir(r)}>
                    editar
                  </button>
                  <button
                    type="button"
                    className="text-xs underline text-[color:var(--erro-texto)]"
                    onClick={() => void apagar(r)}
                  >
                    apagar
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}

        {editando ? (
          <div className="flex flex-col gap-2 rounded-xl border border-[color:var(--borda)] p-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium">O que você lê no alerta do celular</span>
              <input className="campo" value={rotulo} onChange={(e) => setRotulo(e.target.value)} placeholder="perguntou sobre convênio" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium">Expressões que reconhecem isso, separadas por vírgula</span>
              <textarea className="campo" rows={3} value={termos} onChange={(e) => setTermos(e.target.value)} placeholder="aceita convenio, atende pelo plano, meu plano" />
              <span className="text-xs text-[color:var(--tinta-fraca)]">
                Frases, não palavras soltas: “data” aparece em “atualizar meus dados”. Acento e
                maiúscula não importam.
              </span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium">O que o contato lê (opcional)</span>
              <input className="campo" value={fala} onChange={(e) => setFala(e.target.value)} placeholder="Vou chamar uma pessoa para te responder isso." />
              <span className="text-xs text-[color:var(--tinta-fraca)]">Em branco usa a frase padrão do Hugi.</span>
            </label>
            <label className="flex items-center gap-2">
              <span className="text-xs font-medium">Ordem</span>
              <input type="number" className="campo w-20" value={ordem} onChange={(e) => setOrdem(Number(e.target.value))} />
              <span className="text-xs text-[color:var(--tinta-fraca)]">
                Menor vem primeiro. Quando duas casam, é a primeira que escolhe a frase.
              </span>
            </label>
            {erro ? <p role="alert" className="text-sm text-[color:var(--erro-texto)]">{erro}</p> : null}
            <div className="flex gap-2">
              <Botao tipo="primario" onClick={() => void salvar()} desabilitado={ocupado || rotulo.trim() === ""} motivoDeDesabilitar="Escreva o que a regra reconhece.">
                Salvar regra
              </Botao>
              <Botao tipo="secundario" onClick={() => setEditando(null)}>
                Cancelar
              </Botao>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Botao tipo="secundario" onClick={() => abrir(null)}>
              <Plus size={16} /> Nova regra
            </Botao>
            {erro ? <p role="alert" className="text-sm text-[color:var(--erro-texto)]">{erro}</p> : null}
          </div>
        )}
      </div>

      {/* --- A política de mídia ------------------------------------------------------------- */}
      <div className="grid items-start gap-4 lg:grid-cols-2 [&>*]:min-w-0">
        {([
          { campo: "midiaImagem", titulo: "Quando chega uma foto", valor: props.config?.midiaImagem ?? "transbordar" },
          { campo: "midiaArquivo", titulo: "Quando chega um vídeo ou documento", valor: props.config?.midiaArquivo ?? "evasiva" },
        ] as const).map((c) => (
          <div key={c.campo} className="vidro flex flex-col gap-3 p-4">
            <p className="text-sm font-medium">{c.titulo}</p>
            <div className="grid gap-2">
              {POLITICAS.map((o) => (
                <Escolha
                  key={o.id}
                  nome={c.campo}
                  valor={o.id}
                  marcada={c.valor === o.id}
                  aoEscolher={() => props.aoSalvarPolitica({ [c.campo]: o.id })}
                  titulo={o.rotulo}
                  descricao={o.frase}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-[color:var(--tinta-fraca)]">
        O assistente nunca interpreta a imagem — a escolha aqui é só sobre o que ele faz com a
        conversa quando ela chega.
      </p>
    </div>
  );
}

function Numero({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <div>
      <p className="text-2xl font-medium tabular-nums">{valor}</p>
      <p className="text-xs text-[color:var(--tinta-fraca)]">{rotulo}</p>
    </div>
  );
}

/**
 * Procedimentos (§4 da spec 08) — e eles são PROSA, de propósito.
 *
 * *"Obrigar fluxograma para 'explicar preparo de exame' é o jeito clássico de a automação não ser adotada —
 * quem sabe o conteúdo não é quem sabe montar grafo."*
 *
 * Dois campos separados, e a separação não é enfeite: **quando usar** é o que o modelo lê para escolher o
 * procedimento; **as instruções** são o que ele segue depois de escolher. Num campo só, a instrução competiria
 * com a condição na hora da escolha — e o efeito observável é o assistente seguindo o procedimento errado com
 * convicção.
 */
function Procedimentos(props: {
  readonly itens: readonly Procedimento[];
  readonly cliente: QueryClient;
  readonly agenteId: string | null;
}) {
  const [aberto, setAberto] = useState<string | "novo" | null>(null);
  const [nome, setNome] = useState("");
  const [quandoUsar, setQuandoUsar] = useState("");
  const [instrucoes, setInstrucoes] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const abrir = (p: Procedimento | "novo") => {
    setErro(null);
    if (p === "novo") {
      setAberto("novo");
      setNome("");
      setQuandoUsar("");
      setInstrucoes("");
      return;
    }
    setAberto(p.id);
    setNome(p.nome);
    setQuandoUsar(p.quandoUsar);
    setInstrucoes(p.instrucoes);
  };

  const salvar = async () => {
    setOcupado(true);
    setErro(null);
    const r = await salvarProcedimento(props.cliente, props.agenteId, {
      ...(aberto && aberto !== "novo" ? { id: aberto } : {}),
      nome,
      quandoUsar,
      instrucoes,
    });
    setOcupado(false);
    if (r.ok) {
      setAberto(null);
      return;
    }
    setErro(r.falha?.erro.mensagem ?? "Não foi possível salvar.");
  };

  return (
    <div className="vidro flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-medium">Procedimentos <Porque texto="Roteiros em texto, escritos por quem entende do atendimento. O assistente escolhe pelo “quando usar” e segue as instruções." /></p>
        </div>
        <Botao tipo="secundario" onClick={() => abrir("novo")}>
          Novo procedimento
        </Botao>
      </div>

      {props.itens.length === 0 && aberto === null && (
        <p className="text-sm text-[color:var(--tinta-fraca)]">
          Nenhum ainda. Um procedimento é o jeito mais barato de o assistente responder como a sua equipe
          responderia — sem montar fluxo.
        </p>
      )}

      <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
        {props.itens.map((p) => (
          <li key={p.id} className="flex flex-col gap-1 py-2">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-sm font-medium">{p.nome}</span>
              {!p.ativo && <Selo rotulo="Desligado" estado="neutro" />}
              <button
                type="button"
                className="ml-auto text-xs underline text-[color:var(--tinta-fraca)]"
                onClick={() => abrir(p)}
              >
                editar
              </button>
              <button
                type="button"
                className="text-xs underline text-[color:var(--tinta-fraca)]"
                onClick={() => void apagarProcedimento(props.cliente, props.agenteId, p.id)}
              >
                apagar
              </button>
            </div>
            <p className="text-xs text-[color:var(--tinta-fraca)]">Quando: {p.quandoUsar}</p>
          </li>
        ))}
      </ul>

      {aberto !== null && (
        <div className="flex flex-col gap-2 border-t border-[color:var(--borda)] pt-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">Nome</span>
            <input className="campo" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Preparo de exame" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">Quando usar</span>
            <input
              className="campo"
              value={quandoUsar}
              onChange={(e) => setQuandoUsar(e.target.value)}
              placeholder="o contato pergunta como se preparar para um exame"
            />
            <span className="text-xs text-[color:var(--tinta-fraca)]">
              É por esta frase que o assistente decide usar o procedimento. Sem ela, ele aplica o roteiro em
              conversa que não tem nada a ver.
            </span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">O roteiro</span>
            <textarea
              className="campo min-h-32"
              value={instrucoes}
              onChange={(e) => setInstrucoes(e.target.value)}
              placeholder={"1. Confirme qual exame.\n2. Peça jejum de 8 horas.\n3. Ofereça remarcar se ele não puder."}
            />
            <span className="text-xs text-[color:var(--tinta-fraca)]">
              {instrucoes.length}/4000. Ele entra inteiro no prompt de cada mensagem, então o tamanho aparece
              na conta.
            </span>
          </label>
          {erro && <p className="text-xs text-[color:var(--erro-texto)]">{erro}</p>}
          <div className="flex gap-2">
            <Botao onClick={() => void salvar()} desabilitado={ocupado}>
              Salvar
            </Botao>
            <Botao tipo="secundario" onClick={() => setAberto(null)}>
              Cancelar
            </Botao>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Tarefas (§4 da spec 08) — quando o assistente deixa de responder e passa a FAZER.
 *
 * ## A lista de ações é fechada, e a tela diz por quê
 *
 * *"O agente não ganha poder que o atendente não tem — ele chama as mesmas operações."* A tela não tem campo
 * livre de ação: só o seletor com as cinco operações que existem, e cada uma diz o que faz.
 *
 * ## A confirmação nasce LIGADA
 *
 * Uma tarefa que finaliza sozinha, na primeira versão do assistente de um cliente, é a coisa mais rápida de
 * fazer o cliente desligar tudo. O custo de confirmar é uma frase a mais na conversa.
 */
function Tarefas(props: {
  readonly itens: readonly TarefaDoAgente[];
  readonly operacoes: readonly {
    id: string;
    rotulo: string;
    precisa: "fila" | "motivo" | "etiqueta" | null;
    frase: string;
  }[];
  readonly filas: readonly { id: string; nome: string }[];
  readonly motivos: readonly { id: string; nome: string }[];
  readonly etiquetas: readonly { id: string; nome: string }[];
  readonly cliente: QueryClient;
  readonly agenteId: string | null;
}) {
  const [aberto, setAberto] = useState<string | "novo" | null>(null);
  const [nome, setNome] = useState("");
  const [quandoUsar, setQuandoUsar] = useState("");
  const [operacao, setOperacao] = useState("transferir");
  const [alvo, setAlvo] = useState("");
  const [exigeConfirmacao, setExigeConfirmacao] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const escolhida = props.operacoes.find((o) => o.id === operacao);

  const abrir = (t: TarefaDoAgente | "novo") => {
    setErro(null);
    if (t === "novo") {
      setAberto("novo");
      setNome("");
      setQuandoUsar("");
      setOperacao("transferir");
      setAlvo("");
      setExigeConfirmacao(true);
      return;
    }
    setAberto(t.id);
    setNome(t.nome);
    setQuandoUsar(t.quandoUsar);
    setOperacao(t.operacao);
    setAlvo(t.filaId ?? t.motivoId ?? t.etiquetaId ?? "");
    setExigeConfirmacao(t.exigeConfirmacao);
  };

  const salvar = async () => {
    setOcupado(true);
    setErro(null);
    const r = await salvarTarefa(props.cliente, props.agenteId, {
      ...(aberto && aberto !== "novo" ? { id: aberto } : {}),
      nome,
      quandoUsar,
      operacao,
      // O alvo é UM campo na tela e três no banco: qual deles vale é decidido pela operação, e mandar os três
      // preenchidos deixaria lixo na linha quando alguém trocasse a operação.
      filaId: escolhida?.precisa === "fila" ? alvo : null,
      motivoId: escolhida?.precisa === "motivo" ? alvo : null,
      etiquetaId: escolhida?.precisa === "etiqueta" ? alvo : null,
      exigeConfirmacao,
    });
    setOcupado(false);
    if (r.ok) {
      setAberto(null);
      return;
    }
    setErro(r.falha?.erro.mensagem ?? "Não foi possível salvar.");
  };

  const opcoesDoAlvo =
    escolhida?.precisa === "fila"
      ? props.filas
      : escolhida?.precisa === "motivo"
        ? props.motivos
        : escolhida?.precisa === "etiqueta"
          ? props.etiquetas
          : [];

  return (
    <div className="vidro flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Tarefas</p>
          <p className="mt-0.5 text-xs text-[color:var(--tinta-fraca)]">
            O que o assistente pode <strong>fazer</strong>, e não só responder. São as mesmas ações que um
            atendente faz — ele não ganha poder que a sua equipe não tem.
          </p>
        </div>
        <Botao tipo="secundario" onClick={() => abrir("novo")}>
          Nova tarefa
        </Botao>
      </div>

      {props.itens.length === 0 && aberto === null && (
        <p className="text-sm text-[color:var(--tinta-fraca)]">
          Nenhuma ainda. Enquanto não houver tarefa, o relatório mede esforço do assistente — não resultado.
        </p>
      )}

      <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
        {props.itens.map((t) => (
          <li key={t.id} className="flex flex-col gap-1 py-2">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-sm font-medium">{t.nome}</span>
              <span className="text-xs text-[color:var(--tinta-fraca)]">
                {props.operacoes.find((o) => o.id === t.operacao)?.rotulo ?? t.operacao}
              </span>
              {t.exigeConfirmacao ? (
                <Selo rotulo="Pede confirmação" estado="ok" />
              ) : (
                <Selo rotulo="Executa direto" estado="degradado" />
              )}
              {!t.ativa && <Selo rotulo="Desligada" estado="neutro" />}
              <button
                type="button"
                className="ml-auto text-xs underline text-[color:var(--tinta-fraca)]"
                onClick={() => abrir(t)}
              >
                editar
              </button>
              <button
                type="button"
                className="text-xs underline text-[color:var(--tinta-fraca)]"
                onClick={() => void apagarTarefa(props.cliente, props.agenteId, t.id)}
              >
                apagar
              </button>
            </div>
            <p className="text-xs text-[color:var(--tinta-fraca)]">Quando: {t.quandoUsar}</p>
          </li>
        ))}
      </ul>

      {aberto !== null && (
        <div className="flex flex-col gap-2 border-t border-[color:var(--borda)] pt-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">Nome</span>
            <input
              className="campo"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Transferir para financeiro"
            />
            <span className="text-xs text-[color:var(--tinta-fraca)]">
              É por este nome que o assistente chama a tarefa — escreva algo que descreva a ação.
            </span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">Quando usar</span>
            <input
              className="campo"
              value={quandoUsar}
              onChange={(e) => setQuandoUsar(e.target.value)}
              placeholder="o contato quer falar de boleto, nota ou pagamento"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">O que ela faz</span>
            <select
              className="campo"
              value={operacao}
              onChange={(e) => {
                setOperacao(e.target.value);
                setAlvo("");
              }}
            >
              {props.operacoes.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.rotulo}
                </option>
              ))}
            </select>
            {escolhida && <span className="text-xs text-[color:var(--tinta-fraca)]">{escolhida.frase}</span>}
          </label>

          {escolhida?.precisa && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium">
                {escolhida.precisa === "fila" ? "Para qual fila" : escolhida.precisa === "motivo" ? "Com qual motivo" : "Qual etiqueta"}
              </span>
              <select className="campo" value={alvo} onChange={(e) => setAlvo(e.target.value)}>
                <option value="">escolha</option>
                {opcoesDoAlvo.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nome}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1"
              checked={exigeConfirmacao}
              onChange={(e) => setExigeConfirmacao(e.target.checked)}
            />
            <span>
              <span className="text-xs font-medium">Perguntar ao contato antes de executar</span>
              <span className="block text-xs text-[color:var(--tinta-fraca)]">
                Nasce ligado. Uma tarefa que finaliza ou transfere sozinha muda o que a sua equipe vê — e
                desligar isto é escolha sua, não padrão nosso.
              </span>
            </span>
          </label>

          {erro && <p className="text-xs text-[color:var(--erro-texto)]">{erro}</p>}
          <div className="flex gap-2">
            <Botao onClick={() => void salvar()} desabilitado={ocupado}>
              Salvar
            </Botao>
            <Botao tipo="secundario" onClick={() => setAberto(null)}>
              Cancelar
            </Botao>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Diretrizes (§3 da spec 08).
 *
 * ## Uma lista com liga/desliga, e não um campo de texto grande
 *
 * A diferença é o que o cliente consegue fazer: num campo grande, "tirar a regra de escalonamento" significa
 * achar o parágrafo e apagar — e quem apaga não sabe o que perdeu. Numa lista, é um clique que dá para
 * desfazer.
 *
 * **As três de fábrica não podem ser apagadas**, e o botão diz por quê. Elas são o piso de qualquer
 * atendimento; desligar resolve o caso legítimo sem destruir a informação de que existiram.
 */
function Diretrizes(props: {
  readonly itens: readonly Diretriz[];
  readonly tipos: readonly { id: string; rotulo: string; pergunta: string }[];
  readonly cliente: ReturnType<typeof useQueryClient>;
  readonly agenteId: string | null;
}) {
  const [abrindo, setAbrindo] = useState(false);
  const [tipo, setTipo] = useState("uso_de_conteudo");
  const [titulo, setTitulo] = useState("");
  const [corpo, setCorpo] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const salvar = async () => {
    setErro(null);
    const r = await salvarDiretriz(props.cliente, props.agenteId, { tipo, titulo, corpo });
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível salvar.");
      return;
    }
    setTitulo("");
    setCorpo("");
    setAbrindo(false);
  };

  return (
    <div className="vidro flex flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-medium">Diretrizes <Porque texto="As regras que ele segue. Cada uma liga e desliga sozinha — desligar não apaga." /></p>
        </div>
        <Botao tipo="secundario" onClick={() => setAbrindo(!abrindo)}>
          {abrindo ? "Fechar" : "Nova diretriz"}
        </Botao>
      </div>

      {abrindo && (
        <div className="flex flex-col gap-2 rounded-xl bg-[color:var(--realce)] p-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">Tipo</span>
            <select className="campo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {props.tipos.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.rotulo}
                </option>
              ))}
            </select>
            {/* A pergunta que o tipo responde vem do servidor — é vocabulário de produto. */}
            <span className="text-xs text-[color:var(--tinta-fraca)]">
              {props.tipos.find((t) => t.id === tipo)?.pergunta}
            </span>
          </label>
          <input
            className="campo"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Nome da regra, para você reconhecer na lista"
          />
          <textarea
            className="campo min-h-20"
            value={corpo}
            onChange={(e) => setCorpo(e.target.value)}
            placeholder="Escreva a regra como você a explicaria para alguém que vai atender no seu lugar."
          />
          {erro && <p className="text-sm text-[color:var(--erro-texto)]">{erro}</p>}
          <Botao onClick={() => void salvar()} desabilitado={titulo.trim() === "" || corpo.trim() === ""}>
            Salvar diretriz
          </Botao>
        </div>
      )}

      <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
        {props.itens.map((d) => (
          <li key={d.id} className="flex items-start gap-3 py-2">
            <input
              type="checkbox"
              className="mt-1"
              checked={d.ativa}
              onChange={() =>
                void salvarDiretriz(props.cliente, props.agenteId, {
                  id: d.id,
                  tipo: d.tipo,
                  titulo: d.titulo,
                  corpo: d.corpo,
                  ativa: !d.ativa,
                })
              }
            />
            <div className="min-w-0 flex-1">
              <p className={"text-sm " + (d.ativa ? "font-medium" : "text-[color:var(--tinta-fraca)]")}>
                {d.titulo}
                {d.deFabrica && (
                  <span className="ml-2 text-xs font-normal text-[color:var(--tinta-fraca)]">básica</span>
                )}
              </p>
              <p className="text-xs text-[color:var(--tinta-fraca)]">{d.corpo}</p>
            </div>
            <button
              type="button"
              className="shrink-0 text-xs underline text-[color:var(--tinta-fraca)]"
              title={
                d.deFabrica
                  ? "As três básicas não podem ser apagadas — desligue no lugar."
                  : undefined
              }
              disabled={d.deFabrica}
              onClick={() => void apagarDiretriz(props.cliente, props.agenteId, d.id)}
            >
              apagar
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Base de conhecimento (§5).
 *
 * ## O estado do pipeline aparece, mesmo sendo instantâneo hoje
 *
 * §5.1: o cliente sobe um documento, pergunta ao agente, ele não sabe — e sem estado visível a conclusão é
 * *"o produto não funciona"*. Com texto colado o processamento é instantâneo, e a coluna de estado parece
 * exagero. Ela nasce agora porque, quando entrar PDF, a tela que nunca mostrou `recebido` não vai saber
 * desenhá-lo.
 *
 * ## Texto colado, e a tela diz que é isso
 *
 * Upload exige multipart, limite novo, decisão sobre onde o arquivo fica, e extração de PDF. Nenhuma das
 * quatro é sobre conhecimento. Prometer upload e entregar erro seria pior.
 */
function BaseDeConhecimento(props: {
  readonly itens: readonly Conhecimento[];
  readonly cliente: ReturnType<typeof useQueryClient>;
  readonly agenteId: string | null;
}) {
  const [abrindo, setAbrindo] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [corpo, setCorpo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [gerandoDe, setGerandoDe] = useState<string | null>(null);

  const salvar = async () => {
    setErro(null);
    const r = await acrescentarConhecimento(props.cliente, props.agenteId, { titulo, corpo });
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível salvar.");
      return;
    }
    setTitulo("");
    setCorpo("");
    setAbrindo(false);
  };

  const ESTADOS: Record<Conhecimento["estado"], { rotulo: string; estado: "ok" | "degradado" | "neutro" }> = {
    recebido: { rotulo: "Recebido", estado: "neutro" },
    extraido: { rotulo: "Lendo", estado: "neutro" },
    indexado: { rotulo: "Pronto", estado: "ok" },
    falhou: { rotulo: "Falhou", estado: "degradado" },
  };

  return (
    <div className="vidro flex flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-medium">O que ele sabe <Porque texto="Política, preparo de exame, tabela de convênios — o que ele pode citar. Um documento por assunto: assunto misturado é o que faz ele citar a coisa errada." /></p>
        </div>
        <Botao tipo="secundario" onClick={() => setAbrindo(!abrindo)}>
          {abrindo ? "Fechar" : "Acrescentar"}
        </Botao>
      </div>

      {abrindo && (
        <div className="flex flex-col gap-2 rounded-xl bg-[color:var(--realce)] p-3">
          <input
            className="campo"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ex.: Convênios aceitos"
          />
          <textarea
            className="campo min-h-32 text-sm leading-relaxed"
            value={corpo}
            onChange={(e) => setCorpo(e.target.value)}
            placeholder="Cole o texto aqui."
          />
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            Por enquanto é <strong>texto colado</strong>, não arquivo. Cole de um PDF ou de um documento e
            funciona igual — o que falta é a leitura automática do arquivo, e prometer isso agora seria
            prometer o que não existe.
          </p>
          {erro && <p className="text-sm text-[color:var(--erro-texto)]">{erro}</p>}
          <Botao onClick={() => void salvar()} desabilitado={titulo.trim() === "" || corpo.trim() === ""}>
            Acrescentar
          </Botao>
        </div>
      )}

      {props.itens.length === 0 ? (
        <p className="text-sm text-[color:var(--tinta-fraca)]">
          Nada ainda. Sem isto, o assistente só sabe o que está nas instruções.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
          {props.itens.map((c) => (
            <li key={c.id} className="flex items-baseline gap-3 py-2 text-sm">
              <Selo rotulo={ESTADOS[c.estado].rotulo} estado={ESTADOS[c.estado].estado} />
              <span className="min-w-0 flex-1">
                {c.titulo}
                {c.geradoPeloHugi && (
                  // §5.2 regra 5: gerado pelo Hugi aparece DITO, e não como se o cliente tivesse subido.
                  <span className="ml-2 text-xs text-[color:var(--tinta-fraca)]">gerado do seu cadastro</span>
                )}
                {c.motivoDaFalha && (
                  <span className="block text-xs text-[color:var(--erro-texto)]">{c.motivoDaFalha}</span>
                )}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-[color:var(--tinta-fraca)]">
                {c.caracteres.toLocaleString("pt-BR")} caracteres
              </span>
              {c.estado === "indexado" && (
                <button
                  type="button"
                  className="shrink-0 text-xs underline text-[color:var(--tinta-fraca)]"
                  onClick={() => setGerandoDe(gerandoDe === c.id ? null : c.id)}
                >
                  {gerandoDe === c.id ? "fechar perguntas" : "gerar perguntas"}
                </button>
              )}
              <button
                type="button"
                className="shrink-0 text-xs underline text-[color:var(--tinta-fraca)]"
                onClick={() => void apagarConhecimento(props.cliente, props.agenteId, c.id)}
              >
                apagar
              </button>
            </li>
          ))}
        </ul>
      )}

      {gerandoDe && (
        <ParesDoMaterial
          key={gerandoDe}
          material={props.itens.find((c) => c.id === gerandoDe) ?? null}
          cliente={props.cliente}
          agenteId={props.agenteId}
          aoTerminar={() => setGerandoDe(null)}
        />
      )}
    </div>
  );
}

/**
 * Perguntas e respostas geradas de um material (H78).
 *
 * A IA ESCREVE, o dono CONFERE: nada entra na base sem ele marcar e salvar. Os pares vêm editáveis e
 * todos marcados — corrigir uma resposta é o trabalho que esta tela existe para tornar barato. O que ele
 * salva vira um material novo, pelo caminho de sempre, e aparece na lista como qualquer outro.
 */
export function ParesDoMaterial(props: {
  readonly material: Conhecimento | null;
  readonly cliente: ReturnType<typeof useQueryClient>;
  readonly agenteId: string | null;
  readonly aoTerminar: () => void;
}) {
  const [pares, setPares] = useState<{ pergunta: string; resposta: string; marcado: boolean }[] | null>(null);
  const [cortado, setCortado] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  if (!props.material) return null;
  const material = props.material;

  const gerar = async () => {
    setErro(null);
    setGerando(true);
    const r = await gerarParesDoConhecimento(props.agenteId, material.id);
    setGerando(false);
    if (!r.ok) {
      setErro(r.mensagem);
      return;
    }
    if (r.pares.length === 0) {
      setErro("A IA não achou no texto nada que virasse pergunta e resposta. Material muito curto ou sem fatos concretos?");
      return;
    }
    setCortado(r.cortado);
    setPares(r.pares.map((p) => ({ ...p, marcado: true })));
  };

  const mudar = (i: number, campo: "pergunta" | "resposta" | "marcado", valor: string | boolean) =>
    setPares((atual) => atual?.map((p, j) => (j === i ? { ...p, [campo]: valor } : p)) ?? null);

  const escolhidos = (pares ?? []).filter((p) => p.marcado && p.pergunta.trim() && p.resposta.trim());

  const salvar = async () => {
    setErro(null);
    const corpo = escolhidos.map((p) => `P: ${p.pergunta.trim()}\nR: ${p.resposta.trim()}`).join("\n\n");
    const r = await acrescentarConhecimento(props.cliente, props.agenteId, {
      titulo: `Perguntas e respostas — ${material.titulo}`,
      corpo,
    });
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível salvar.");
      return;
    }
    props.aoTerminar();
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-[color:var(--realce)] p-3" aria-label="Perguntas e respostas geradas">
      <p className="text-sm font-medium">Perguntas e respostas de “{material.titulo}”</p>
      {!pares && (
        <>
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            A IA lê este material e escreve até 30 perguntas que um cliente faria, com a resposta tirada do
            texto. Nada entra na base até você conferir e salvar.
          </p>
          {erro && <p className="text-sm text-[color:var(--erro-texto)]">{erro}</p>}
          <Botao onClick={() => void gerar()} desabilitado={gerando}>
            {gerando ? "Gerando…" : "Gerar perguntas e respostas"}
          </Botao>
        </>
      )}
      {pares && (
        <>
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            Confira cada resposta: a IA pode ter entendido errado. Desmarque o que não serve e corrija o
            que precisa.
            {cortado && " O material é longo e só o começo foi lido — divida-o por assunto para cobrir o resto."}
          </p>
          <ol className="flex flex-col gap-3">
            {pares.map((p, i) => (
              <li key={i} className="flex gap-2">
                <input
                  type="checkbox"
                  className="mt-2"
                  checked={p.marcado}
                  aria-label={`Usar a pergunta ${i + 1}`}
                  onChange={(e) => mudar(i, "marcado", e.target.checked)}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <input
                    className="campo text-sm"
                    value={p.pergunta}
                    aria-label={`Pergunta ${i + 1}`}
                    onChange={(e) => mudar(i, "pergunta", e.target.value)}
                  />
                  <textarea
                    className="campo min-h-16 text-sm"
                    value={p.resposta}
                    aria-label={`Resposta ${i + 1}`}
                    onChange={(e) => mudar(i, "resposta", e.target.value)}
                  />
                </div>
              </li>
            ))}
          </ol>
          {erro && <p className="text-sm text-[color:var(--erro-texto)]">{erro}</p>}
          <Botao onClick={() => void salvar()} desabilitado={escolhidos.length === 0}>
            {`Salvar ${escolhidos.length} na base`}
          </Botao>
        </>
      )}
    </div>
  );
}

/**
 * Simulações — o coração da spec 08 (§6).
 *
 * ## Hoje não existe nada entre editar o agente e o paciente
 *
 * §6.1: isso é o equivalente a `push --force` em produção, feito por quem não sabe que está fazendo deploy.
 *
 * ## E a tela diz duas coisas desconfortáveis
 *
 * 1. **Sem provedor, nada roda** — e o resultado é `nao rodou`, nunca verde. Um painel de teste que aprova
 *    sem testar produz a confiança que ele deveria estar verificando.
 * 2. **Verde não é garantia**: *"passou nos casos que você escreveu"*. Teste de comportamento de modelo é
 *    amostragem, não prova (§6.4).
 */
function Simulacoes(props: {
  readonly itens: readonly Simulacao[];
  readonly esperas: readonly { id: string; rotulo: string; pega: string }[];
  readonly temProvedor: boolean;
  readonly cliente: ReturnType<typeof useQueryClient>;
  readonly agenteId: string | null;
}) {
  const [abrindo, setAbrindo] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [espera, setEspera] = useState("nao_afirma");
  const [esperaTexto, setEsperaTexto] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [rodando, setRodando] = useState(false);

  const salvar = async () => {
    setErro(null);
    const r = await salvarSimulacao(props.cliente, props.agenteId, {
      titulo,
      mensagens: mensagem.split("\n").filter((m) => m.trim() !== ""),
      espera,
      esperaTexto,
    });
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível salvar.");
      return;
    }
    setTitulo("");
    setMensagem("");
    setEsperaTexto("");
    setAbrindo(false);
  };

  const passaram = props.itens.filter((s) => s.ultimoResultado === "passou").length;
  const falharam = props.itens.filter((s) => s.ultimoResultado === "falhou").length;

  return (
    <div className="vidro flex flex-col gap-3 p-4">
      {/* `flex-wrap` mais o `min-w-0`: sem eles, a 375px o par "Rodar"/"Novo caso" (144px) não cabe
          ao lado do título e vaza 10px do cartão — o mesmo padrão que o `.cabeca` do `@hugi/ui`
          recebeu em 05/09. Com eles os botões descem, e em tela larga nada muda. */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-medium">Casos de teste <Porque texto="O que você espera do assistente em situações específicas. É o que existe entre você editar e o seu cliente ler." /></p>
        </div>
        <div className="flex gap-2">
          <Botao
            tipo="secundario"
            onClick={async () => {
              setRodando(true);
              await rodarSimulacoes(props.cliente, props.agenteId);
              setRodando(false);
            }}
            desabilitado={rodando || props.itens.length === 0}
            {...(props.itens.length === 0 ? { motivoDeDesabilitar: "Escreva um caso primeiro." } : {})}
          >
            Rodar
          </Botao>
          <Botao tipo="secundario" onClick={() => setAbrindo(!abrindo)}>
            {abrindo ? "Fechar" : "Novo caso"}
          </Botao>
        </div>
      </div>

      {!props.temProvedor && (
        <p className="text-xs text-[color:var(--atencao-texto)]">
          Sem a chave do provedor de linguagem no servidor, rodar marca cada caso como{" "}
          <strong>não rodou</strong> — e não como aprovado. Um painel de teste que aprova sem testar é pior
          que não ter painel.
        </p>
      )}

      {abrindo && (
        <div className="flex flex-col gap-2 rounded-xl bg-[color:var(--realce)] p-3">
          <input
            className="campo"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ex.: perguntou preço de um exame que não está na tabela"
          />
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">O que o contato escreve (uma mensagem por linha)</span>
            <textarea
              className="campo min-h-20"
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              placeholder={"oi\nquanto custa a ressonância?"}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">E o que se espera</span>
            <select className="campo" value={espera} onChange={(e) => setEspera(e.target.value)}>
              {props.esperas.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.rotulo}
                </option>
              ))}
            </select>
            {/* Cada expectativa diz qual DEFEITO ela pega — é o que faz a pessoa escolher a certa. */}
            <span className="text-xs text-[color:var(--tinta-fraca)]">
              {props.esperas.find((e) => e.id === espera)?.pega}
            </span>
          </label>
          {(espera === "responde_com" || espera === "nao_afirma") && (
            <input
              className="campo"
              value={esperaTexto}
              onChange={(e) => setEsperaTexto(e.target.value)}
              placeholder={espera === "nao_afirma" ? "o que ele NÃO pode dizer. Ex.: R$" : "o que a resposta precisa mencionar"}
            />
          )}
          {erro && <p className="text-sm text-[color:var(--erro-texto)]">{erro}</p>}
          <Botao onClick={() => void salvar()} desabilitado={titulo.trim() === "" || mensagem.trim() === ""}>
            Salvar caso
          </Botao>
        </div>
      )}

      {props.itens.length > 0 && (
        <>
          {(passaram > 0 || falharam > 0) && (
            <p className="text-xs text-[color:var(--tinta-fraca)]">
              {passaram} passaram, {falharam} falharam — e <strong>passar não é garantia</strong>: passou nos
              casos que você escreveu.
            </p>
          )}
          <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
            {props.itens.map((s) => (
              <li key={s.id} className="flex items-start gap-3 py-2 text-sm">
                <Selo
                  rotulo={
                    s.ultimoResultado === "passou"
                      ? "Passou"
                      : s.ultimoResultado === "falhou"
                        ? "Falhou"
                        : s.ultimoResultado === "nao_rodou"
                          ? "Não rodou"
                          : "Nunca rodou"
                  }
                  estado={
                    s.ultimoResultado === "passou" ? "ok" : s.ultimoResultado === "falhou" ? "degradado" : "neutro"
                  }
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{s.titulo}</p>
                  <p className="text-xs text-[color:var(--tinta-fraca)]">
                    {props.esperas.find((e) => e.id === s.espera)?.rotulo}
                    {s.esperaTexto ? `: “${s.esperaTexto}”` : ""}
                  </p>
                  {s.ultimoDetalhe && (
                    <p className="mt-0.5 text-xs text-[color:var(--tinta-fraca)]">{s.ultimoDetalhe}</p>
                  )}
                  {s.desatualizada && (
                    // Resultado de uma versão anterior é resultado velho. Mostrá-lo como atual é a forma
                    // mais eficiente de um painel de teste mentir.
                    <p className="mt-0.5 text-xs text-[color:var(--atencao-texto)]">
                      O assistente mudou depois desta rodada — este resultado é de antes.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className="shrink-0 text-xs underline text-[color:var(--tinta-fraca)]"
                  onClick={() => void apagarSimulacao(props.cliente, props.agenteId, s.id)}
                >
                  apagar
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * QUANDO ELE RESPONDE — o "quando" e o "onde" na mesma aba (spec 23, D2).
 *
 * Duas escolhas grandes (Fora do horário / Sempre) e o número. Quem LIGA é o primário do cabeçalho, que grava e
 * publica num clique; esta aba não tem botão próprio de publicar, para a tela ter uma ação primária só.
 *
 * As três guardas de publicar continuam, agora no "Ligar": sem conexão ativa não liga (e a aba diz onde conectar);
 * **substituir outro assistente pede confirmação nomeada** (a frase vem do servidor); e desligar num número
 * (despublicar) continua aqui, porque é o botão que salva quando a mudança de ontem começou a responder errado.
 */
function QuandoResponde(props: {
  readonly atende: QuandoAtende;
  readonly aoMudarAtende: (a: QuandoAtende) => void;
  readonly conexoes: readonly { readonly id: string; readonly rotulo: string }[];
  readonly conexaoAlvo: string;
  readonly aoEscolherConexao: (id: string) => void;
  readonly publicacoes: readonly PublicacaoDoAgente[];
  readonly cliente: QueryClient;
  readonly agenteId: string | null;
  readonly carregandoConexoes: boolean;
}) {
  const [, navegar] = useLocation();
  const [erro, setErro] = useState<string | null>(null);
  // Fora do horário e Sempre na frente, grandes; Desligado é a terceira, e é o padrão de nascença.
  const ordem: readonly QuandoAtende[] = ["fora_do_horario", "sempre", "nunca"];

  const desligarEm = async (conexaoId: string) => {
    setErro(null);
    const r = await despublicarAgente(props.cliente, props.agenteId, conexaoId);
    if (!r.ok) setErro(r.falha?.erro.mensagem ?? "Não foi possível desligar neste número.");
  };

  return (
    <div className="hugi-agente-quando">
      <section className="v2-cartao hugi-agente-quando-cartao" aria-labelledby="hugi-agente-quando-titulo">
        <h2 id="hugi-agente-quando-titulo">Quando ele responde</h2>
        <div className="hugi-agente-quando-escolhas">
          {ordem.map((id) => {
            const opcao = QUANDO.find((o) => o.id === id)!;
            return (
              <Escolha
                key={id}
                nome="atende"
                valor={id}
                marcada={props.atende === id}
                aoEscolher={() => props.aoMudarAtende(id)}
                titulo={opcao.rotulo}
                descricao={opcao.frase}
              />
            );
          })}
        </div>
      </section>

      <section className="v2-cartao hugi-agente-quando-cartao" aria-labelledby="hugi-agente-onde-titulo">
        <h2 id="hugi-agente-onde-titulo">Em qual número</h2>
        {props.carregandoConexoes ? (
          <p className="hugi-agente-fraco">Carregando os números…</p>
        ) : props.conexoes.length === 0 ? (
          <div className="hugi-agente-quando-linha">
            <p className="hugi-agente-fraco">Nenhum número conectado.</p>
            <Botao tipo="secundario" onClick={() => navegar("/canais/conectar")}>
              <Smartphone size={16} /> Conectar um número
            </Botao>
          </div>
        ) : props.conexoes.length === 1 ? (
          <p className="hugi-agente-numero">{props.conexoes[0]!.rotulo}</p>
        ) : (
          <select
            className="campo"
            aria-label="Número em que ele responde"
            value={props.conexaoAlvo}
            onChange={(e) => props.aoEscolherConexao(e.target.value)}
          >
            <option value="">Escolha o número</option>
            {props.conexoes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.rotulo}
              </option>
            ))}
          </select>
        )}

        {props.publicacoes.length > 0 ? (
          <ul className="hugi-agente-no-ar" aria-label="No ar agora">
            {props.publicacoes.map((p) => (
              <li key={p.conexaoId}>
                <Radio size={14} aria-hidden="true" />
                <span className="hugi-agente-no-ar-texto">
                  <span className="hugi-agente-no-ar-nome">{identificadorLegivel(p.conexaoRotulo)}</span>
                  <span className="hugi-agente-fraco">desde {new Date(p.publicadoEm).toLocaleString("pt-BR")} · versão {p.versao}</span>
                </span>
                <button type="button" className="hugi-link-de-campo" onClick={() => void desligarEm(p.conexaoId)}>
                  desligar neste número
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {erro ? <p role="alert" className="v2-erro">{erro}</p> : null}
      </section>
    </div>
  );
}

/**
 * Quem é o agente desta tela (0057).
 *
 * Uma empresa pode ter vários — recepção, cobrança, boas-vindas —, cada um com instruções, material,
 * tarefas e publicação próprios. O seletor é uma faixa de ladrilhos, um por agente, com o que decide
 * na hora de escolher: se está no ar (em quantos números) e se tem instruções. "Novo agente" pede só
 * o nome; o resto se configura nas abas, como sempre.
 *
 * Arquivar é `perigo` e confirma antes: o servidor recusa arquivar o único agente e o que está no ar,
 * e a frase dele chega inteira à tela.
 */
function SeletorDeAgentes(props: {
  readonly agentes: readonly ResumoDoAgente[];
  readonly escolhido: string | null;
  readonly cliente: QueryClient;
  readonly aoEscolher: (id: string | null) => void;
}) {
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const atual = props.agentes.find((a) => a.id === props.escolhido) ?? props.agentes[0] ?? null;

  const criar = async () => {
    setOcupado(true);
    setErro(null);
    const r = await criarAgente(props.cliente, { nome: nome.trim() });
    setOcupado(false);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível criar o agente.");
      return;
    }
    setCriando(false);
    setNome("");
    if (r.id) props.aoEscolher(r.id);
  };

  const arquivar = async () => {
    if (!atual) return;
    if (!window.confirm(`Arquivar o agente '${atual.nome}'? Ele sai da lista e do fluxo, e o material dele fica guardado.`)) return;
    setErro(null);
    const r = await arquivarAgente(props.cliente, atual.id);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível arquivar.");
      return;
    }
    props.aoEscolher(null);
  };

  return (
    <div className="vidro flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-[color:var(--tinta-fraca)]">
          {props.agentes.length === 1 ? "1 agente" : `${props.agentes.length} agentes`}
        </span>
        <Porque
          texto={
            "Cada agente tem instruções, material, tarefas e casos de teste próprios, e é publicado por número. " +
            "O fluxo escolhe qual deles assume um passo — o de boas-vindas chama a recepção, o de cobrança " +
            "chama o financeiro."
          }
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {props.agentes.map((a) => {
          const ativo = atual?.id === a.id;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => props.aoEscolher(a.id)}
              aria-pressed={ativo}
              className={
                "flex items-center gap-3 rounded-[9px] border px-3 py-2 text-left transition " +
                (ativo
                  ? "border-[color:var(--acao)] bg-[color:var(--acao-clara)]"
                  : "border-[color:var(--borda)] hover:bg-[color:var(--vidro-controle)]")
              }
            >
              <Ladrilho tom="ia">
                <Bot size={18} />
              </Ladrilho>
              <span className="flex flex-col">
                <span className="text-sm font-medium">{a.nome}</span>
                <span className="text-xs text-[color:var(--tinta-fraca)]">
                  {a.publicacoes > 0
                    ? `No ar em ${a.publicacoes} número${a.publicacoes === 1 ? "" : "s"}`
                    : a.temInstrucoes
                      ? "Pronto para publicar"
                      : "Sem instruções"}
                  {a.principal ? " · principal" : ""}
                </span>
              </span>
            </button>
          );
        })}
        {!criando && (
          <button
            type="button"
            onClick={() => setCriando(true)}
            className="flex items-center gap-2 rounded-[9px] border border-dashed border-[color:var(--borda)] px-3 py-2 text-sm text-[color:var(--acao-texto)] hover:bg-[color:var(--vidro-controle)]"
          >
            <Plus size={16} /> Novo agente
          </button>
        )}
      </div>
      {criando && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">Nome do novo agente</span>
            <input
              className="campo w-64"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void criar();
                if (e.key === "Escape") setCriando(false);
              }}
              placeholder="Cobrança"
              autoFocus
            />
          </label>
          <Botao
            tipo="primario"
            onClick={() => void criar()}
            desabilitado={ocupado || nome.trim() === ""}
            motivoDeDesabilitar={ocupado ? "Criando…" : "Escreva o nome do agente."}
          >
            Criar
          </Botao>
          <Botao tipo="secundario" onClick={() => setCriando(false)}>
            Cancelar
          </Botao>
        </div>
      )}
      {/* Agente no ar não sai (o servidor recusa) — o botão só aparece quando arquivar é possível. */}
      {props.agentes.length > 1 && atual && atual.publicacoes === 0 && (
        <div className="flex items-center gap-2">
          <Botao tipo="perigo" onClick={() => void arquivar()}>
            Arquivar '{atual.nome}'
          </Botao>
        </div>
      )}
      {erro && <p className="text-sm text-[color:var(--erro-texto)]">{erro}</p>}
    </div>
  );
}
